import React from 'react'

const featuresLeft = [
  {
    title: 'Skill Assessment',
    desc: 'Identify precise technical gaps and baseline metrics.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  },
  {
    title: 'Personalized Mentorship',
    desc: '1-on-1 expert curriculum guidance & mock interviews.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
  },
  {
    title: 'Career-Focused Pathways',
    desc: 'Syllabus aligned strictly to enterprise hiring demands.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
  }
]

const featuresRight = [
  {
    title: 'Flexible Learning Pace',
    desc: 'Study modules dynamically match your work schedule.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  },
  {
    title: 'Customized Projects',
    desc: 'Build scalable portfolio-ready cloud applications.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
  },
  {
    title: 'Real-Time Updates',
    desc: 'Curriculum patches ensuring absolute latest industry tech.',
    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
  }
]

const progressMetrics = [
  { label: 'Skill Growth Velocity', color: 'from-[#ff4b4b] to-[#ff007f]', width: 'w-[92%]' },
  { label: 'Iterative Step-by-Step Mastery', color: 'from-[#ffc107] to-[#ff9800]', width: 'w-[85%]' },
  { label: 'Infrastructure Development', color: 'from-[#10b981] to-[#047857]', width: 'w-[88%]' },
  { label: 'Architectural Brilliance', color: 'from-[#0b3b8f] to-[#00cfff]', width: 'w-[95%]' }
]

export default function Roadmap() {
  return (
    <section id="roadmap" className="relative py-28 md:py-36 bg-white font-inter overflow-hidden border-t border-slate-100 min-h-[500px] lg:min-h-[650px] flex items-center">
      
      {/* Absolute Gradient Blends for Ambience */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00cfff]/5 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#0b3b8f]/5 rounded-full blur-[150px] pointer-events-none"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <div className="inline-flex items-center gap-2 bg-[#0b3b8f]/5 border border-[#0b3b8f]/10 px-5 py-2 rounded-full mb-6">
            <span className="w-2.5 h-2.5 rounded-full bg-[#0b3b8f] animate-[ping_3s_linear_infinite]"></span>
            <span className="text-xs font-extrabold tracking-widest text-[#0b3b8f] uppercase">Curriculum Architecture</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
            Personalized <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0b3b8f] to-[#00cfff]">Learning Roadmap</span>
          </h2>
          <p className="text-lg text-slate-600 font-medium leading-relaxed">
            Master Full-Stack architectures and DevOps CI/CD pipelines with a meticulously guided path: core assessments, active mentorship, deep project-based practice, and scalable certifications—dynamically customized to your precise learning pace.
          </p>
        </div>

        {/* Triple Column Layout */}
        <div className="grid lg:grid-cols-[1fr_1.5fr_1fr] gap-8 md:gap-12 items-center mb-24">
          
          {/* Left Cards */}
          <div className="order-2 lg:order-1 flex flex-col gap-6">
            {featuresLeft.map((f, i) => (
              <div key={i} className="group bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_2px_20px_rgba(0,0,0,0.03)] hover:shadow-xl hover:border-[#00cfff]/30 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
                <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-[#0b3b8f]/[0.02] to-transparent rounded-bl-full pointer-events-none transition-transform group-hover:scale-150"></div>
                <div className="w-12 h-12 rounded-2xl bg-[#0b3b8f]/5 text-[#0b3b8f] flex items-center justify-center mb-4 group-hover:bg-[#0b3b8f] group-hover:text-white transition-colors duration-300">
                  {f.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-[#0b3b8f] transition-colors">{f.title}</h3>
                <p className="text-sm text-slate-500 font-medium leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Central Holographic Image Frame */}
          <div className="order-1 lg:order-2 relative mx-auto w-full max-w-lg lg:max-w-none perspective-1000">
            <div className="relative rounded-3xl bg-slate-900 p-2 shadow-2xl shadow-[#0b3b8f]/20 hover:-translate-y-2 transition-transform duration-700">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0b3b8f] via-[#00cfff] to-[#4b00cc] rounded-3xl opacity-20 blur-xl animate-pulse"></div>
              <img 
                src="/DevOps-.webp" 
                alt="Cloud Learning Roadmap Pathway" 
                width="512"
                height="384"
                className="relative w-full h-auto rounded-[22px] border border-slate-700 object-cover z-10"
              />
              
              {/* Overlay Glass Elements */}
              <div className="absolute -bottom-6 -right-6 lg:-right-10 bg-white/90 backdrop-blur-md border border-slate-200 px-6 py-4 rounded-2xl shadow-xl z-20 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Success Rate</div>
                  <div className="text-2xl font-black text-slate-800">98.4%</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Cards */}
          <div className="order-3 flex flex-col gap-6">
            {featuresRight.map((f, i) => (
              <div key={i} className="group bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_2px_20px_rgba(0,0,0,0.03)] hover:shadow-xl hover:border-[#00cfff]/30 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
                <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-[#0b3b8f]/[0.02] to-transparent rounded-bl-full pointer-events-none transition-transform group-hover:scale-150"></div>
                <div className="w-12 h-12 rounded-2xl bg-[#0b3b8f]/5 text-[#0b3b8f] flex items-center justify-center mb-4 group-hover:bg-[#00cfff] group-hover:text-white transition-colors duration-300">
                  {f.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-[#0b3b8f] transition-colors">{f.title}</h3>
                <p className="text-sm text-slate-500 font-medium leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          
        </div>

        {/* Performance Telemetry Grid */}
        <div className="max-w-4xl mx-auto bg-slate-50 rounded-3xl p-8 md:p-12 border border-slate-100 shadow-sm relative overflow-hidden">
          {/* subtle mesh background inside telemetry */}
          <div className="absolute right-0 top-0 w-64 h-64 bg-gradient-to-br from-[#00cfff]/10 to-transparent rounded-full blur-[80px]"></div>
          
          <h3 className="text-xl font-extrabold text-slate-800 mb-8 flex items-center gap-3">
            <svg className="w-6 h-6 text-[#0b3b8f]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
            Live Candidate Telemetry
          </h3>

          <div className="grid gap-6">
            {progressMetrics.map((b) => (
              <div key={b.label} className="relative z-10">
                <div className="flex justify-between text-sm font-bold text-slate-600 mb-2">
                  <span>{b.label}</span>
                  <span className="text-slate-600">{b.width.match(/\[(.*?)\]/)[1]}</span>
                </div>
                <div className="h-3 w-full bg-slate-200 rounded-full overflow-hidden shadow-inner">
                  <div className={`h-full bg-gradient-to-r ${b.color} ${b.width} rounded-full animate-pulse shadow-[0_0_10px_rgba(0,0,0,0.1)] transition-all duration-1000`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  )
}
